"use client";

import AnswerSummary from "@/components/AnswerSummary";
import { MUHURAT_HEADLINES } from "./Muhurat.copy";

import { useState, useEffect, useCallback, startTransition, type ReactNode } from "react";
import Link from "next/link";
import { getMuhurat } from "@/lib/api/muhurat";
import type { MuhuratResponse } from "@/lib/api/muhurat";
import { type PlaceResult } from "@ishvaram/ui/hooks";
import PlaceAutocomplete from "@/components/PlaceAutocomplete";
import { trackCalculatorUsed } from "@/lib/analytics-free-tools";
import { toDateString, formatDate } from "@/components/muhurat/types";
import { Collapsible } from "@ishvaram/ui/Collapsible";

// The result renderers + report bridge were a next/dynamic chunk (2026-07-16
// trim pass). Even with `ssr: true` that is a Suspense boundary, and when the
// chunk resolves after the shell flushes the server HTML carries the loading
// card with the real result in a hidden streamed segment, which the
// server-versus-hydrated certification rejects. A static import puts the
// seeded default-city result in the HTML itself; the LoadingCard reserve
// still covers a browser-side refetch (rule 58 — no 0px collapse).
import MuhuratResults from "./MuhuratResultsSection";

const EVENT_MUHURAT_GUIDES = [
  { slug: "marriage", en: "Marriage Muhurat", hi: "विवाह मुहूर्त" },
  { slug: "griha-pravesh", en: "Griha Pravesh Muhurat", hi: "गृह प्रवेश मुहूर्त" },
  { slug: "mundan", en: "Mundan Muhurat", hi: "मुंडन मुहूर्त" },
  { slug: "naamkaran", en: "Naamkaran Muhurat", hi: "नामकरण मुहूर्त" },
  { slug: "annaprashan", en: "Annaprashan Muhurat", hi: "अन्नप्राशन मुहूर्त" },
  { slug: "vehicle", en: "Vehicle Purchase Muhurat", hi: "वाहन खरीद मुहूर्त" },
  { slug: "business", en: "Business Start Muhurat", hi: "व्यापार मुहूर्त" },
  { slug: "property", en: "Property Purchase Muhurat", hi: "भूमि खरीद मुहूर्त" },
  { slug: "bhoomi-pujan", en: "Bhoomi Pujan Muhurat", hi: "भूमि पूजन मुहूर्त" },
  { slug: "travel", en: "Travel Muhurat", hi: "यात्रा मुहूर्त" },
  { slug: "job-joining", en: "Job Joining Muhurat", hi: "नौकरी ज्वाइनिंग मुहूर्त" },
  { slug: "shop-opening", en: "Shop Opening Muhurat", hi: "दुकान खोलने का मुहूर्त" },
  { slug: "vidyarambh", en: "Vidyarambh Muhurat", hi: "विद्यारंभ मुहूर्त" },
  { slug: "surgery", en: "Surgery Muhurat", hi: "ऑपरेशन मुहूर्त" },
  { slug: "engagement", en: "Engagement Muhurat", hi: "सगाई मुहूर्त" },
];

export default function MuhuratClient({
  defaultLanguage = "en",
  crossLinks,
  initialDate,
  initialResult = null,
  answer,
}: {
  defaultLanguage?: "en" | "hi";
  crossLinks: ReactNode;
  /**
   * The AEO direct answer, composed on the SERVER and passed in as a string.
   *
   * It renders directly under the <h1> MuhuratHeader owns, above the picker.
   * It used to sit after this whole client, 737 visible words past the
   * heading, so an extractor reading the first 40-60 words got form chrome
   * instead of the answer. Passed as a prop rather than imported here so the
   * answer module and its reference datasets stay out of the client bundle.
   */
  answer?: string;
  /** IST date the server seeded (see the page's `getInitialMuhurat`). */
  initialDate?: string;
  /**
   * Today's windows for the default city, fetched on the server so they sit
   * in the HTML itself. The browser refetches only when the seeker changes
   * the date or the city; a surface that fetched on mount rendered
   * "Calculating muhurat..." on the server and a result (or a fetch error)
   * after hydration, which the server-versus-hydrated certification rejects.
   */
  initialResult?: MuhuratResponse | null;
}) {
  const [loading, setLoading] = useState(!initialResult);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<MuhuratResponse | null>(initialResult);
  const [selectedDate, setSelectedDate] = useState<string>(
    () => initialDate ?? toDateString(new Date())
  );
  const [cityInput, setCityInput] = useState("New Delhi");
  const [selectedCity, setSelectedCity] = useState<PlaceResult | null>(null);

  const fetchMuhurat = useCallback(async (dateStr: string, city: PlaceResult | null) => {
    startTransition(() => {
      setLoading(true);
      setError(null);
    });
    try {
      const response = city
        ? await getMuhurat(dateStr, city.display, city.latitude, city.longitude, city.timezone)
        : await getMuhurat(dateStr, "New Delhi");
      startTransition(() => setResult(response));
      trackCalculatorUsed({ calculator: "muhurat" });
    } catch (err) {
      startTransition(() =>
        setError(err instanceof Error ? err.message : "Failed to load muhurat")
      );
    } finally {
      startTransition(() => setLoading(false));
    }
  }, []);

  useEffect(() => {
    // The seeded default-city view needs no fetch; anything else does.
    if (
      initialResult &&
      result === initialResult &&
      selectedDate === initialDate &&
      !selectedCity
    ) {
      return;
    }
    fetchMuhurat(selectedDate, selectedCity);
  }, [selectedDate, selectedCity, fetchMuhurat, initialDate, initialResult, result]);

  const displayDate = new Date(selectedDate + "T00:00:00");

  // Count slots by type
  const shubhCount = result?.slots.filter((s) => s.classification === "shubh").length ?? 0;
  const ashubhCount = result?.slots.filter((s) => s.classification === "ashubh").length ?? 0;
  const neutralCount = (result?.slots.length ?? 0) - shubhCount - ashubhCount;

  return (
    <div className="intent-interface-timing-first sacred-bg min-h-[calc(100vh-130px)]">
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-12 sm:py-16">
        <MuhuratHeader
          language={defaultLanguage}
          displayDate={displayDate}
          selectedCity={selectedCity}
        />
        {answer ? <AnswerSummary answer={answer} /> : null}

        <MuhuratPicker
          selectedDate={selectedDate}
          cityInput={cityInput}
          onDateChange={setSelectedDate}
          onCityInputChange={(value) => {
            setCityInput(value);
            setSelectedCity(null);
          }}
          onCitySelect={(place) => {
            setCityInput(place.display);
            setSelectedCity(place);
          }}
        />

        {loading && <LoadingCard />}

        <ErrorCard
          error={error}
          loading={loading}
          onRetry={() => fetchMuhurat(selectedDate, selectedCity)}
        />

        {result && !loading && (
          <MuhuratResults
            result={result}
            selectedDate={selectedDate}
            selectedCity={selectedCity}
            shubhCount={shubhCount}
            ashubhCount={ashubhCount}
            neutralCount={neutralCount}
            defaultLanguage={defaultLanguage}
          />
        )}

        {/* The Hindi page carries its own "शुभ मुहूर्त क्या है?" section. */}
        {defaultLanguage === "hi" ? null : <MuhuratSeoContent />}
        <EventMuhuratGuides language={defaultLanguage} />

        <div className="mt-12">{crossLinks}</div>
      </div>
    </div>
  );
}

function MuhuratHeader({
  language,
  displayDate,
  selectedCity,
}: {
  language: "en" | "hi";
  displayDate: Date;
  selectedCity: PlaceResult | null;
}) {
  return (
    <div className="text-center mb-10">
      <p className="text-[11px] font-bold uppercase tracking-[0.25em] mb-3 text-intent-brand">
        Shubh Muhurat
      </p>
      <h1 className="text-[28px] sm:text-[36px] font-bold leading-tight font-serif text-ish-dark">
        {MUHURAT_HEADLINES[language]}
      </h1>
      <p className="mt-2 text-body text-ish-text-muted">{formatDate(displayDate)}</p>
      <p className="mt-1 text-caption text-ish-text-faint">
        Rahu Kaal, Yamagandam & auspicious timings for{" "}
        {selectedCity ? selectedCity.name : "New Delhi"}
      </p>
      <div className="mt-4 flex flex-wrap justify-center gap-x-5 gap-y-2 text-[11px] font-medium text-ish-text-faint">
        <TrustSignal icon="shield" label="Accurate Vedic calculations" />
        <TrustSignal icon="clock" label="City-wise Timings" />
        <TrustSignal icon="user" label="100% Free" />
      </div>
    </div>
  );
}

function TrustSignal({ icon, label }: { icon: "shield" | "clock" | "user"; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        {icon === "shield" && <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />}
        {icon === "clock" && (
          <>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </>
        )}
        {icon === "user" && (
          <>
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </>
        )}
      </svg>
      {label}
    </span>
  );
}

function MuhuratPicker({
  selectedDate,
  cityInput,
  onDateChange,
  onCityInputChange,
  onCitySelect,
}: {
  selectedDate: string;
  cityInput: string;
  onDateChange: (value: string) => void;
  onCityInputChange: (value: string) => void;
  onCitySelect: (place: PlaceResult) => void;
}) {
  return (
    <div className="card-sacred px-4 py-4 mb-8 space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <label
          htmlFor="muhurat-date"
          className="text-body-small font-semibold shrink-0 text-ish-dark"
        >
          Date:
        </label>
        <input
          id="muhurat-date"
          type="date"
          value={selectedDate}
          onChange={(event) => onDateChange(event.target.value)}
          className="input-sacred rounded-lg px-3 py-1.5 text-body text-ish-dark"
        />
      </div>
      <div>
        <label className="block text-body-small font-semibold mb-1.5 text-ish-dark">City:</label>
        <PlaceAutocomplete
          id="muhurat-city"
          value={cityInput}
          onChange={onCityInputChange}
          onSelect={onCitySelect}
          placeholder="Start typing a city (e.g. Mumbai, Delhi)"
          required={false}
          className="input-sacred w-full rounded-lg px-3 py-2 text-body"
        />
      </div>
    </div>
  );
}

// In server HTML this card means the page's seed fetch failed (a seeded render
// starts with loading=false), so it is marked degraded: the public HTML cache
// never stores it and the live correctness check reports it.
function LoadingCard() {
  return (
    <div className="card-sacred p-8 text-center" data-ishvaram-degraded="muhurat-seed">
      <span className="inline-block w-6 h-6 border-2 border-stone-300 border-t-stone-600 rounded-full animate-spin mb-3" />
      <p className="text-body text-ish-text-muted">Calculating muhurat...</p>
    </div>
  );
}

function ErrorCard({
  error,
  loading,
  onRetry,
}: {
  error: string | null;
  loading: boolean;
  onRetry: () => void;
}) {
  if (!error || loading) return null;
  return (
    <div className="card-sacred p-6 text-center border-red-100 bg-red-50/50">
      <p className="text-body text-red-600">{error}</p>
      <button onClick={onRetry} className="mt-3 text-body-small font-semibold text-intent-brand">
        Try Again
      </button>
    </div>
  );
}

function MuhuratSeoContent() {
  return (
    <div className="mt-12">
      <Collapsible title="What is Shubh Muhurat?" defaultOpen headingLevel="h2">
        <div className="space-y-3 text-body leading-relaxed text-ish-text-muted">
          <p>
            <strong className="text-ish-dark">Shubh Muhurat</strong> (auspicious timing) is a
            concept in Vedic astrology where specific time periods of the day are considered
            favorable or unfavorable for starting important activities. This is determined by
            analyzing the Panchang &mdash; the five elements of the Hindu calendar: Tithi,
            Nakshatra, Yoga, Karana, and Vara.
          </p>
          <p>
            <strong className="text-ish-dark">Rahu Kaal</strong> is the most widely observed
            inauspicious period. It occurs daily for approximately 1.5 hours and is ruled by Rahu,
            the shadow planet associated with obstacles and delays. Most Hindus avoid starting new
            ventures, signing contracts, or beginning journeys during Rahu Kaal.
          </p>
          <p>
            <strong className="text-ish-dark">Yamagandam</strong> is another inauspicious period
            ruled by Yama, the lord of death. <strong className="text-ish-dark">Gulika Kaal</strong>{" "}
            (ruled by Saturn) is also avoided for auspicious beginnings. Together with Rahu Kaal,
            these three periods are the key inauspicious windows to avoid each day.
          </p>
          <p>
            Our calculator uses real planetary positions from Swiss Ephemeris to compute the exact
            Panchang elements at each hour, giving you an accurate picture of auspicious and
            inauspicious timings throughout the day.
          </p>
        </div>
      </Collapsible>
    </div>
  );
}

function EventMuhuratGuides({ language }: { language: "en" | "hi" }) {
  const hi = language === "hi";
  return (
    <div className="mt-8">
      <Collapsible
        title={hi ? "कार्य अनुसार मुहूर्त गाइड" : "Event Muhurat Guides"}
        headingLevel="h2"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
          {EVENT_MUHURAT_GUIDES.map((event) => (
            <Link
              key={event.slug}
              href={`/muhurat/${event.slug}/`}
              className="card-sacred group flex items-start gap-3.5 p-4 transition-all duration-200"
            >
              <div className="min-w-0">
                <span className="block text-body font-semibold text-ish-dark">
                  {hi ? event.hi : event.en}
                </span>
                <span className="block text-caption mt-0.5 text-ish-text-faint">
                  {hi ? event.en : event.hi}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </Collapsible>
    </div>
  );
}
